import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Metadata, RunView } from '@memberjunction/core';
import { ResourceData, MJUserSettingEntity } from '@memberjunction/core-entities';
import { RegisterClass, UUIDsEqual } from '@memberjunction/global';
import { BaseResourceComponent } from '@memberjunction/ng-shared';
import { skip, takeUntil } from 'rxjs/operators';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';
import { BCMembershipService } from '@bluecypress/bcsaas-entities-extended';
import { BC_ROLE_LEVELS } from '@bluecypress/bcsaas-core';
import { BCSaaSOrganizationRoleEntity } from '@bluecypress/bcsaas-entities';
import { IzzyMetadataEngine } from 'mj_generatedentities';
import { BCSaaSChannelEntity } from '@bluecypress/bcsaas-entities';
import { BCSaaSAIConfigurationEntity } from '@bluecypress/bcsaas-entities';
import { AdminPanelStatus } from '../admin-panel.interface';
import { IzzyAuthService } from '../../services/izzy-auth.service';
import { IzzyContextService } from '../../services/izzy-context.service';
import { OrgSelectorComponent } from '../org-selector/org-selector.component';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';

/**
 * Admin section definition
 */
interface AdminSection {
  id: string;
  label: string;
  icon: string;
  description: string;
  category: 'org' | 'advanced' | 'restricted';
}

/**
 * Status indicator type for navigation items
 */
type NavStatus = 'good' | 'warning' | 'error' | 'none';

/**
 * Status info with tooltip
 */
interface NavStatusInfo {
  status: NavStatus;
  tooltip: string;
}

/**
 * Tree node for hierarchical organization display
 */
interface OrgTreeNode {
  org: mjBizAppsCommonOrganizationEntity;
  children: OrgTreeNode[];
  expanded: boolean;
  depth: number;
  hasChildren: boolean;
}

/**
 * Form data for creating/editing an organization
 */
interface OrgForm {
  name: string;
  parentId: string | null;
  description: string;
}

/**
 * User settings structure for persistence
 */
interface AdminSettings {
  isNavCollapsed: boolean;
  selectedSection?: string;
  selectedOrgId?: string;
  selectedChannelId?: string;
  advancedExpanded?: boolean;
  restrictedExpanded?: boolean;
}

/**
 * Administration Component
 *
 * Unified admin console with stylish left navigation sidebar.
 * Consolidates all administrative functions:
 * - Organization management (Settings, Organizations, Members, Channels, Actions)
 * - System administration (API Keys) - for customer sys admins
 * - Restricted (Contacts, Roles, Plans, Channel Types, Scopes, Action Gallery, Private Grants) - IsOwner only
 */
@Component({
  standalone: false,
  selector: 'izzy-administration',
  templateUrl: './administration.component.html',
  styleUrls: ['./administration.component.css']
})
@RegisterClass(BaseResourceComponent, "IzzyAdministrationResource")
export class IzzyAdministrationResource extends BaseResourceComponent implements OnInit, OnDestroy {
  protected sections: AdminSection[] = [
    // Organization Management — the everyday admin surface. Keep this list
    // short: an org admin should see only what they actually use.
    { id: 'settings', label: 'Settings', icon: 'fa-sliders', description: 'Organization settings', category: 'org' },
    { id: 'members', label: 'Members', icon: 'fa-users', description: 'Organization members', category: 'org' },
    { id: 'channels', label: 'Channels', icon: 'fa-satellite-dish', description: 'Communication channels & run history', category: 'org' },
    { id: 'ticketing', label: 'Ticketing', icon: 'fa-ticket', description: 'Teams, SLAs, routing, skills, automation & macros', category: 'org' },
    { id: 'ai', label: 'AI', icon: 'fa-robot', description: 'AI configuration & credentials', category: 'org' },
    // NOTE: 'channel-runs' is no longer a section — run history lives as a
    // tab inside Channels (channelsTab). Saved ids are migrated in
    // applyPendingSelections.

    // Advanced — developer-ish plumbing, collapsed by default
    { id: 'org-actions', label: 'Actions', icon: 'fa-bolt', description: 'Organization actions', category: 'advanced' },
    { id: 'credentials', label: 'Credentials', icon: 'fa-key', description: 'API credentials & secrets', category: 'advanced' },
    { id: 'api-keys', label: 'API Keys', icon: 'fa-code', description: 'API key management', category: 'advanced' },
    { id: 'setup-agent', label: 'Setup', icon: 'fa-wand-magic-sparkles', description: 'AI-powered knowledge base setup', category: 'advanced' },

    // Restricted - internal only (IsOwner = true)
    { id: 'action-gallery', label: 'Action Gallery', icon: 'fa-table-cells', description: 'Browse & manage actions', category: 'restricted' },
    { id: 'organizations', label: 'Organizations', icon: 'fa-building', description: 'Master organizations directory', category: 'restricted' },
    { id: 'contacts', label: 'Contacts', icon: 'fa-address-book', description: 'Master contact directory', category: 'restricted' },
    { id: 'contact-roles', label: 'Roles', icon: 'fa-user-tag', description: 'Contact role definitions', category: 'restricted' },
    { id: 'plans', label: 'Plans', icon: 'fa-layer-group', description: 'Subscription plans', category: 'restricted' },
    { id: 'channel-types', label: 'Channel Types', icon: 'fa-plug', description: 'Channel type definitions', category: 'restricted' },
    { id: 'credential-types', label: 'Credential Types', icon: 'fa-fingerprint', description: 'Credential schema definitions', category: 'restricted' },
    { id: 'scopes', label: 'Scopes', icon: 'fa-shield-halved', description: 'API permission scopes', category: 'restricted' },
    { id: 'private-grants', label: 'Private Grants', icon: 'fa-key', description: 'Private action grants', category: 'restricted' }
  ];

  protected selectedSection: AdminSection;
  protected isInitializing: boolean = true; // True until user settings and initial data are loaded
  protected isNavCollapsed: boolean = false;

  /** Advanced nav group (developer-ish sections) — collapsed by default, persisted */
  protected advancedExpanded: boolean = false;

  /** Restricted nav group (internal-only sections) — collapsed by default, persisted */
  protected restrictedExpanded: boolean = false;

  toggleRestrictedGroup(): void {
    this.restrictedExpanded = !this.restrictedExpanded;
    this.saveUserSettings();
  }

  /** Active tab within the Channels section ('channels' | 'runs') */
  protected channelsTab: 'channels' | 'runs' = 'channels';

  toggleAdvancedGroup(): void {
    this.advancedExpanded = !this.advancedExpanded;
    this.saveUserSettings();
  }
  /** Platform developer (member of the internal MeetIzzy.ai org) — NOT org-level Owner. Gates the restricted sections. */
  protected isSystemDeveloper: boolean = false;
  protected hasAdminAccess: boolean = false; // True if user is Admin or Owner in the selected org

  // Set of organization IDs where the current user has >= ADMIN level. Populated
  // once (async) in loadOrgData() so getUserOrganizations() — which is called
  // synchronously from several places — can filter the dropdown to only the orgs
  // the user can actually administer. Null = not yet loaded (don't filter yet).
  private adminOrgIds: Set<string> | null = null;

  // ========== Org/Channel Context ==========
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected channels: BCSaaSChannelEntity[] = [];
  protected selectedOrg: mjBizAppsCommonOrganizationEntity | null = null;
  protected selectedOrganizationId: string | null = null;
  protected selectedChannel: BCSaaSChannelEntity | null = null;
  protected isChannelDropdownOpen: boolean = false;
  protected hasMultipleOrgs: boolean = false;

  // ========== System Settings Modal ==========
  protected isSystemSettingsOpen: boolean = false;

  // ========== Status Cache ==========
  private statusCache: Map<string, NavStatusInfo> = new Map();
  protected aiConfigurations: BCSaaSAIConfigurationEntity[] = [];

  // ========== Org Tree for System Settings Modal ==========
  protected orgTree: OrgTreeNode[] = [];

  // ========== Org Management ==========
  protected isEditingOrg: boolean = false;
  protected isCreatingOrg: boolean = false;
  protected isSavingOrg: boolean = false;
  protected editingOrgId: string | null = null;
  protected orgForm: OrgForm = { name: '', parentId: null, description: '' };
  protected orgModalError: string = '';
  protected orgModalSuccess: string = '';
  protected confirmDeleteOrgVisible: boolean = false;
  protected deleteOrgId: string | null = null;
  protected deleteOrgName: string = '';

  // ========== ViewChild References ==========
  @ViewChild('orgSelector') orgSelector?: OrgSelectorComponent;

  // ========== User Settings Persistence ==========
  private readonly userSettingsKey = 'Izzy.Administration';
  private userSetting: MJUserSettingEntity | null = null;

  // ========== Deep Linking State ==========
  private _pendingSectionId: string | null = null;
  private _pendingOrganizationId: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private authService: IzzyAuthService,
    private contextService: IzzyContextService
  ) {
    super();
    this.selectedSection = this.sections[0];
    // isSystemDeveloper will be set in loadOrgData() after checking internal-org membership
    this.isSystemDeveloper = false;
  }

  async GetResourceDisplayName(data: ResourceData): Promise<string> {
    return 'Administration';
  }

  async GetResourceIconClass(data: ResourceData): Promise<string> {
    return 'fa-solid fa-sliders';
  }

  async ngOnInit(): Promise<void> {
    // Load user settings first (lowest priority - can be overridden)
    await this.loadUserSettings();

    // Load org/channel data
    await this.loadOrgData();

    // Check for navigation configuration from OpenNavItemByName (takes precedence)
    this.handleNavigationConfiguration();

    // Subscribe to query param changes for deep linking and bookmarking
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.handleQueryParams(params);
    });

    // Apply pending selections (priority: nav config > URL params > user settings > first org)
    this.applyPendingSelections();

    // Publish the org this section actually resolved to the shared context so the header
    // switcher shows what the page loaded. On a hard refresh the switcher boots first and seeds
    // "first org"; the old "only if empty" guard then let this section's URL/settings-driven org
    // differ from the pill indefinitely. The service's no-op setter prevents echo loops.
    if (this.selectedOrg) {
      this.contextService.selectedOrganizationId = this.selectedOrg.ID;
    }

    // Check admin access AFTER org is selected
    await this.updateAdminAccess();

    // Set org header for server-side queries (after all metadata is loaded)
    if (this.selectedOrg) {
      GraphQLDataProvider.Instance.SetDynamicHeader('x-organization-id', this.selectedOrg.ID);
    }

    // Calculate initial status indicators
    this.refreshStatusIndicators();

    // React to org changes made in other Izzy sections while this tab stays alive.
    // skip(1) ignores the BehaviorSubject replay — the initial selection above
    // already consulted the context.
    this.contextService.orgChanges$
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe(orgId => this.onContextOrgChanged(orgId));

    // Initialization complete - safe to render
    this.isInitializing = false;

    this.NotifyLoadComplete();
  }

  /**
   * Apply an org change that originated in another Izzy section.
   * A null context is ignored, and orgs the user cannot administer
   * (not in this section's filtered list) leave the selection unchanged.
   */
  private onContextOrgChanged(orgId: string | null): void {
    if (!orgId || UUIDsEqual(orgId, this.selectedOrg?.ID)) {
      return;
    }
    const org = this.organizations.find(o => UUIDsEqual(o.ID, orgId));
    if (org) {
      void this.onOrgSelectorChange(org);
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  // ========== User Settings Persistence ==========

  private async loadUserSettings(): Promise<void> {
    try {
      const md = new Metadata();
      const currentUserId = md.CurrentUser.ID;

      const rv = new RunView();
      const result = await rv.RunView<MJUserSettingEntity>({
        EntityName: 'MJ: User Settings',
        ExtraFilter: `Setting = '${this.userSettingsKey}' AND UserID = '${currentUserId}'`,
        ResultType: 'entity_object'
      });

      if (result?.Success && result.Results.length > 0) {
        this.userSetting = result.Results[0];
        const settings = JSON.parse(this.userSetting.Value || '{}') as AdminSettings;

        // Apply settings (can be overridden by URL params or nav config later)
        if (settings.isNavCollapsed !== undefined) {
          this.isNavCollapsed = settings.isNavCollapsed;
        }
        if (settings.advancedExpanded !== undefined) {
          this.advancedExpanded = settings.advancedExpanded;
        }
        if (settings.restrictedExpanded !== undefined) {
          this.restrictedExpanded = settings.restrictedExpanded;
        }
        // Store pending values - will be applied after data loads
        if (settings.selectedSection && !this._pendingSectionId) {
          this._pendingSectionId = settings.selectedSection;
        }
        if (settings.selectedOrgId && !this._pendingOrganizationId && !this.contextService.selectedOrganizationId) {
          this._pendingOrganizationId = settings.selectedOrgId;
        }
      }
    } catch (error) {
      console.error('Administration: Error loading user settings:', error);
    }

    // Shared context (org picked in another Izzy section) beats this section's own
    // saved settings — and must be consulted even when no settings record exists yet.
    if (!this._pendingOrganizationId && this.contextService.selectedOrganizationId) {
      this._pendingOrganizationId = this.contextService.selectedOrganizationId;
    }
  }

  private async saveUserSettings(): Promise<void> {
    try {
      const md = new Metadata();
      const settings: AdminSettings = {
        isNavCollapsed: this.isNavCollapsed,
        selectedSection: this.selectedSection?.id,
        selectedOrgId: this.selectedOrg?.ID,
        selectedChannelId: this.selectedChannel?.ID,
        advancedExpanded: this.advancedExpanded,
        restrictedExpanded: this.restrictedExpanded
      };

      if (!this.userSetting) {
        this.userSetting = await md.GetEntityObject<MJUserSettingEntity>('MJ: User Settings');
        this.userSetting.Setting = this.userSettingsKey;
        this.userSetting.UserID = md.CurrentUser.ID;
      }

      this.userSetting.Value = JSON.stringify(settings);
      await this.userSetting.Save();
    } catch (error) {
      console.error('Administration: Error saving user settings:', error);
    }
  }

  // ========== Navigation Configuration & Deep Linking ==========

  /**
   * Handle navigation configuration passed via OpenNavItemByName
   * Configuration is passed through this.Data.Configuration by MJ's navigation system
   */
  private handleNavigationConfiguration(): void {
    const config = this.Data?.Configuration;
    if (!config) return;

    if (config['sectionId']) {
      this._pendingSectionId = config['sectionId'] as string;
    }
    if (config['organizationId']) {
      this._pendingOrganizationId = config['organizationId'] as string;
    }
  }

  /**
   * Handle query params for deep linking from browser URL
   * Supports bookmarking and direct URL access
   */
  private handleQueryParams(params: Record<string, string>): void {
    // Only set if not already set by navigation configuration
    if (params['section'] && !this._pendingSectionId) {
      this._pendingSectionId = params['section'];
    }
    if (params['organizationId'] && !this._pendingOrganizationId) {
      this._pendingOrganizationId = params['organizationId'];
    }

    // Apply immediately if we have pending selections
    if (this._pendingSectionId || this._pendingOrganizationId) {
      this.applyPendingSelections();
    }
  }

  /**
   * Apply pending selections from navigation config, query params, or user settings
   */
  /** Legacy admin section ids that were consolidated into the single 'ticketing' section. */
  private static readonly TICKETING_LEGACY_SECTION_IDS = [
    'ticket-teams', 'sla-policies', 'assignment-rules', 'skills', 'automation-rules', 'macros',
  ];

  private applyPendingSelections(): void {
    // Apply section selection (already set from user settings if available).
    if (this._pendingSectionId) {
      // Migrate a saved/bookmarked id from the six former ticketing sections to the consolidated one,
      // so old user settings / deep links still land somewhere meaningful instead of the default.
      if (IzzyAdministrationResource.TICKETING_LEGACY_SECTION_IDS.includes(this._pendingSectionId)) {
        this._pendingSectionId = 'ticketing';
      }
      // Channel Runs is now a tab inside Channels — migrate saved ids/deep links.
      if (this._pendingSectionId === 'channel-runs') {
        this._pendingSectionId = 'channels';
        this.channelsTab = 'runs';
      }
      const found = this.sections.find(s => s.id === this._pendingSectionId);
      // Restricted sections are internal-only: don't honor a deep link or
      // stale saved id for non-system users (the nav already hides them,
      // but the panel itself must not render either).
      if (found && !(found.category === 'restricted' && !this.isSystemDeveloper)) {
        this.selectedSection = found;
      }
      this._pendingSectionId = null;
    }

    // Apply organization selection (already set from user settings if available)
    if (this._pendingOrganizationId) {
      const org = this.organizations.find(o => o.ID === this._pendingOrganizationId);
      if (org) {
        this.selectedOrg = org;
        this.selectedOrganizationId = org.ID;

        // Also set first channel for this org
        const orgChannels = this.getOrgChannels(org);
        if (orgChannels.length > 0) {
          this.selectedChannel = orgChannels[0];
        }
      }
      this._pendingOrganizationId = null;
    }

    // Update URL to reflect current state
    this.updateQueryParams();
  }

  /**
   * Update URL query params without changing route path
   */
  private updateQueryParams(): void {
    const params = new URLSearchParams();

    if (this.selectedSection && this.selectedSection.id !== 'settings') {
      params.set('section', this.selectedSection.id);
    }

    const path = this.location.path().split('?')[0];
    const queryString = params.toString();
    const newUrl = queryString ? `${path}?${queryString}` : path;
    this.location.replaceState(newUrl);
  }

  /** Min org plan level for ticketing (Pro = 20) — mirrors TICKET_MANAGEMENT_MIN_PLAN_LEVEL in
   *  izzy-application/ProcessorAgent so the Ticketing admin section follows the same paid-add-on gate
   *  as the Tickets nav tab and the auto-create pipeline. */
  private static readonly TICKETING_MIN_PLAN_LEVEL = 20;

  /** True when the selected org is on a plan that includes Ticket Management (Pro+). */
  private get ticketingEnabledForSelectedOrg(): boolean {
    const orgId = this.selectedOrg?.ID;
    if (!orgId) return false;
    const plan = IzzyMetadataEngine.Instance.GetOrganizationPlan(orgId);
    return (plan?.Level ?? 0) >= IzzyAdministrationResource.TICKETING_MIN_PLAN_LEVEL;
  }

  get orgSections(): AdminSection[] {
    return this.sections.filter(s => {
      if (s.category !== 'org') return false;
      // Ticketing admin is a Pro+ add-on — hide it for orgs without ticketing.
      if (s.id === 'ticketing' && !this.ticketingEnabledForSelectedOrg) return false;
      return true;
    });
  }

  get advancedSections(): AdminSection[] {
    return this.sections.filter(s => s.category === 'advanced');
  }

  get restrictedSections(): AdminSection[] {
    return this.sections.filter(s => s.category === 'restricted');
  }

  selectSection(section: AdminSection): void {
    this.selectedSection = section;
    this.saveUserSettings();
    // Update URL for deep linking / bookmarking
    this.updateQueryParams();
  }

  toggleNav(): void {
    this.isNavCollapsed = !this.isNavCollapsed;
    this.saveUserSettings();
  }

  /**
   * Expands the nav and opens the org selector dropdown.
   * Called when clicking the collapsed org indicator.
   */
  expandNavAndOpenOrgSelector(): void {
    this.isNavCollapsed = false;
    this.saveUserSettings();
    // Use setTimeout to wait for the nav to expand and org selector to render
    setTimeout(() => {
      this.orgSelector?.openDropdown();
    }, 100);
  }

  // ========== Organization/Channel Context ==========

  /**
   * Load organization and channel data from IzzyMetadataEngine
   */
  private async loadOrgData(): Promise<void> {
    try {
      const md = new Metadata();
      await IzzyMetadataEngine.Instance.Config(true, md.CurrentUser);
      const engine = IzzyMetadataEngine.Instance;
      const currentContactId = engine.CurrentContactID;

      // Check if current user is a member of the internal MeetIzzy.ai organization
      await this.checkInternalOrgMembership();

      // Resolve which orgs the user can actually administer (>= ADMIN level), so
      // the dropdown only lists orgs they can switch into. Owners skip this — they
      // see everything. Single service call; results cached in adminOrgIds.
      await this.loadAdminOrgIds();

      // Filter organizations to only those the user can administer
      this.organizations = this.getUserOrganizations();

      this.channels = engine.Channels || [];
      this.aiConfigurations = engine.AIConfigurations || [];
      this.hasMultipleOrgs = this.organizations.length > 1;

      // Build org tree for hierarchical display
      this.buildOrgTree();

      // Default to first org if no pending selection from user settings/URL
      if (!this._pendingOrganizationId && this.organizations.length > 0) {
        this.selectedOrg = this.organizations[0];
        this.selectedOrganizationId = this.selectedOrg.ID;
        // Also set first channel
        const orgChannels = this.getOrgChannels(this.selectedOrg);
        if (orgChannels.length > 0) {
          this.selectedChannel = orgChannels[0];
        }
      }
    } catch (error) {
      console.error('Administration: Error loading org data:', error);
    }
  }

  /**
   * Handle organizations changed event from the Organizations component.
   * Refreshes the org selector dropdown to reflect changes.
   */
  async onOrganizationsChanged(): Promise<void> {
    // Re-read organizations from the engine (already refreshed by OrganizationsComponent)
    const engine = IzzyMetadataEngine.Instance;
    const currentContactId = engine.CurrentContactID;

    // Memberships may have changed (e.g. a newly created org auto-adds the user as
    // Owner); re-resolve admin orgs before filtering the dropdown.
    await this.loadAdminOrgIds();
    this.organizations = this.getUserOrganizations();

    this.buildOrgTree();

    // Refresh the org selector dropdown
    await this.orgSelector?.refresh();
  }

  /**
   * Handle settings navigation from the Organizations component
   * Switch to settings section with the specified organization
   */
  async onNavigateToSettings(organizationId: string): Promise<void> {
    // Find the organization
    let org = this.organizations.find(o => o.ID === organizationId);

    // If org not found, it may be newly created - refresh from engine cache
    if (!org) {
      const engine = IzzyMetadataEngine.Instance;
      const currentContactId = engine.CurrentContactID;

      // Refresh our local organizations list from the engine cache
      this.organizations = this.getUserOrganizations();

      // Rebuild org tree with updated data
      this.buildOrgTree();

      // Try finding the org again
      org = this.organizations.find(o => o.ID === organizationId);
      if (!org) return; // Still not found, bail out
    }

    // Select the organization
    this.selectedOrg = org;
    this.selectedOrganizationId = organizationId;

    // Switch to settings section
    const settingsSection = this.sections.find(s => s.id === 'settings');
    if (settingsSection) {
      this.selectSection(settingsSection);
    }
  }

  /**
   * Check if the current user is a system developer (member of internal org).
   * This grants access to the restricted section.
   * Uses IzzyAuthService for centralized auth logic.
   */
  private async checkInternalOrgMembership(): Promise<void> {
    this.isSystemDeveloper = await this.authService.isSystemDeveloper();
    console.log(`Administration: User ${this.isSystemDeveloper ? 'IS' : 'is NOT'} a system developer - restricted access ${this.isSystemDeveloper ? 'enabled' : 'disabled'}`);
  }

  /**
   * Resolve the set of org IDs where the current user has >= ADMIN level, so the
   * admin dropdown only lists orgs they can actually switch into. Owners (system
   * developers) administer everything, so we skip the lookup for them.
   *
   * One server-side, RLS-applied call (GetOrganizationMemberships) — this is the
   * same authoritative source updateAdminAccess() gates on, just resolved up-front
   * for ALL of the user's orgs rather than one-at-a-time on selection.
   */
  private async loadAdminOrgIds(): Promise<void> {
    if (this.isSystemDeveloper) {
      this.adminOrgIds = null; // system developers see all orgs; no filtering
      return;
    }
    const contactId = IzzyMetadataEngine.Instance.CurrentContactID;
    if (!contactId) {
      this.adminOrgIds = new Set<string>();
      return;
    }

    // Preferred: the middleware-stamped tenant context. GetOrganizationMemberships
    // runs a RunView from the browser, where its SkipTenantFilter flag doesn't
    // survive the GraphQL transport — since BCSaaS v1.5 that returns only the
    // current context org's membership, collapsing this set to one org.
    const stamped = IzzyMetadataEngine.Instance.CurrentUserTenantMemberships;
    if (stamped) {
      this.adminOrgIds = new Set<string>(
        stamped
          .filter(m => m.level >= BC_ROLE_LEVELS.ADMIN)
          .map(m => m.organizationID)
      );
      return;
    }

    try {
      const memberships = await BCMembershipService.GetOrganizationMemberships(
        contactId,
        Metadata.Provider.CurrentUser
      );
      this.adminOrgIds = new Set<string>(
        memberships
          .filter(m => m.level >= BC_ROLE_LEVELS.ADMIN)
          .map(m => m.organizationID)
      );
    } catch (error) {
      console.error('Administration: Error loading admin org memberships:', error);
      // Fail closed — an empty set hides all orgs rather than showing un-enterable
      // ones. The per-selection HasMembershipLevel check is still the hard gate.
      this.adminOrgIds = new Set<string>();
    }
  }

  /**
   * Get the organizations the current user can administer (shown in the dropdown).
   * System developers see all orgs; everyone else sees only orgs where they hold
   * >= ADMIN level (adminOrgIds), so they aren't offered orgs that would block
   * them on selection. Per-org access is still hard-gated in updateAdminAccess().
   */
  private getUserOrganizations(): mjBizAppsCommonOrganizationEntity[] {
    const engine = IzzyMetadataEngine.Instance;
    if (this.isSystemDeveloper) {
      return engine.Organizations || [];
    }
    const contactId = engine.CurrentContactID;
    if (!contactId) return [];
    const orgs = engine.GetUserOrganizations(contactId);
    // adminOrgIds null means levels not yet resolved — don't over-filter; the
    // HasMembershipLevel gate still protects selection in that window.
    if (!this.adminOrgIds) return orgs;
    return orgs.filter(o => this.adminOrgIds!.has(o.ID));
  }

  /**
   * Check if user has Level >= ADMIN in the currently selected org.
   * Delegates to BCMembershipService.HasMembershipLevel — single server-side
   * query with RLS applied. Updates hasAdminAccess flag accordingly.
   */
  private async updateAdminAccess(): Promise<void> {
    console.log('[AdminAccess] check: isSystemDeveloper=', this.isSystemDeveloper, 'selectedOrg=', this.selectedOrg?.Name, 'orgID=', this.selectedOrg?.ID);
    if (this.isSystemDeveloper) {
      this.hasAdminAccess = true;
      console.log('[AdminAccess] granted via isSystemDeveloper');
      return;
    }
    if (!this.selectedOrg) {
      this.hasAdminAccess = false;
      console.log('[AdminAccess] denied: no selectedOrg');
      return;
    }
    const contactId = IzzyMetadataEngine.Instance.CurrentContactID;
    if (!contactId) {
      this.hasAdminAccess = false;
      console.log('[AdminAccess] denied: no contactId');
      return;
    }
    // Preferred: the middleware-stamped tenant context (cross-org by design).
    // HasMembershipLevel from the browser is tenant-filtered since BCSaaS v1.5
    // (its SkipTenantFilter flag doesn't survive the GraphQL transport), so it
    // returns false for any org other than the current context org.
    const stampedLevel = IzzyMetadataEngine.Instance.GetCurrentUserMembershipLevel(this.selectedOrg.ID);
    if (stampedLevel !== null) {
      this.hasAdminAccess = stampedLevel >= BC_ROLE_LEVELS.ADMIN;
      console.log('[AdminAccess] via TenantContext level=', stampedLevel, 'result=', this.hasAdminAccess);
      return;
    }

    console.log('[AdminAccess] calling HasMembershipLevel(', contactId, this.selectedOrg.ID, 'ADMIN=', BC_ROLE_LEVELS.ADMIN, ')');
    this.hasAdminAccess = await BCMembershipService.HasMembershipLevel(
      contactId,
      this.selectedOrg.ID,
      BC_ROLE_LEVELS.ADMIN,
      Metadata.Provider.CurrentUser
    );
    console.log('[AdminAccess] result=', this.hasAdminAccess);
  }

  /**
   * Get channels for a specific organization
   */
  getOrgChannels(org: mjBizAppsCommonOrganizationEntity | null): BCSaaSChannelEntity[] {
    if (!org) return [];
    return this.channels.filter(c => c.OrganizationID === org.ID);
  }

  /**
   * Get initials from org name for avatar
   */
  getOrgInitials(name: string): string {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2);
  }

  /**
   * Handler for the shared OrgSelectorComponent.
   * Receives mjBizAppsCommonOrganizationEntity | null (null not used here since allowAll=false)
   */
  async onOrgSelectorChange(org: mjBizAppsCommonOrganizationEntity | null): Promise<void> {
    this.selectedOrg = org;
    this.selectedOrganizationId = org?.ID || null;
    this.contextService.selectedOrganizationId = org?.ID || null;
    this.selectedChannel = null; // Reset channel when org changes
    await this.updateAdminAccess();
    if (org) {
      GraphQLDataProvider.Instance.SetDynamicHeader('x-organization-id', org.ID);
    }
    // If the new org hides the currently selected section (e.g. Ticketing on a non-Pro org),
    // fall back to the first visible org section so we don't render a hidden/empty panel.
    if (this.selectedSection.id === 'ticketing' && !this.ticketingEnabledForSelectedOrg) {
      this.selectedSection = this.orgSections[0] ?? this.sections[0];
    }
    this.refreshStatusIndicators();
    this.saveUserSettings();
  }

  /**
   * Select an organization (called by query param changes or modal)
   */
  selectOrg(org: mjBizAppsCommonOrganizationEntity): void {
    this.onOrgSelectorChange(org);
  }

  /**
   * Select a channel within current org
   */
  selectChannel(channel: BCSaaSChannelEntity | null): void {
    this.selectedChannel = channel;
    this.isChannelDropdownOpen = false;
    this.saveUserSettings();
  }

  /**
   * Toggle channel dropdown
   */
  toggleChannelDropdown(event: Event): void {
    event.stopPropagation();
    this.isChannelDropdownOpen = !this.isChannelDropdownOpen;
  }

  /**
   * Close channel dropdown (called on document click)
   */
  closeDropdowns(): void {
    this.isChannelDropdownOpen = false;
  }

  // ========== System Settings Modal ==========

  /**
   * Open system settings modal
   */
  openSystemSettings(): void {
    this.isSystemSettingsOpen = true;
  }

  /**
   * Close system settings modal
   */
  closeSystemSettings(): void {
    this.isSystemSettingsOpen = false;
  }

  // ========== Org Tree for Hierarchical Display ==========

  /**
   * Build hierarchical tree structure from flat org list
   */
  private buildOrgTree(): void {
    const orgMap = new Map<string, OrgTreeNode>();

    // First pass: create nodes for all orgs
    for (const org of this.organizations) {
      orgMap.set(org.ID, {
        org,
        children: [],
        expanded: true,
        depth: 0,
        hasChildren: false
      });
    }

    // Second pass: build parent-child relationships
    const rootNodes: OrgTreeNode[] = [];
    for (const org of this.organizations) {
      const node = orgMap.get(org.ID)!;
      const parentId = org.ParentID;

      if (parentId && orgMap.has(parentId)) {
        const parentNode = orgMap.get(parentId)!;
        parentNode.children.push(node);
        parentNode.hasChildren = true;
      } else {
        rootNodes.push(node);
      }
    }

    // Third pass: calculate depths
    const setDepths = (nodes: OrgTreeNode[], depth: number) => {
      for (const node of nodes) {
        node.depth = depth;
        setDepths(node.children, depth + 1);
      }
    };
    setDepths(rootNodes, 0);

    // Sort children by name at each level
    const sortChildren = (nodes: OrgTreeNode[]) => {
      nodes.sort((a, b) => a.org.Name.localeCompare(b.org.Name));
      for (const node of nodes) {
        sortChildren(node.children);
      }
    };
    sortChildren(rootNodes);

    this.orgTree = rootNodes;
  }

  /**
   * Get flattened org tree nodes respecting expansion state
   */
  get flattenedOrgTree(): OrgTreeNode[] {
    const result: OrgTreeNode[] = [];
    const flatten = (nodes: OrgTreeNode[]) => {
      for (const node of nodes) {
        result.push(node);
        if (node.expanded && node.children.length > 0) {
          flatten(node.children);
        }
      }
    };
    flatten(this.orgTree);
    return result;
  }

  /**
   * Toggle expansion of an org tree node
   */
  toggleOrgExpand(node: OrgTreeNode, event: Event): void {
    event.stopPropagation();
    node.expanded = !node.expanded;
  }

  // ========== Status Indicators ==========

  /**
   * Get status indicator for a navigation section
   */
  getNavStatus(sectionId: string): NavStatusInfo {
    return this.statusCache.get(sectionId) || { status: 'none', tooltip: '' };
  }

  /**
   * Refresh all status indicators based on current org context
   */
  private refreshStatusIndicators(): void {
    this.statusCache.clear();

    if (!this.selectedOrg) return;

    // AI section status - check for active AI configurations
    const aiStatus = this.calculateAIStatus();
    this.statusCache.set('ai', aiStatus);

    // Channels section status
    const channelsStatus = this.calculateChannelsStatus();
    this.statusCache.set('channels', channelsStatus);

    // Members section - no status needed (always available)
    // Settings - no status needed
    // Credentials - would need separate data load
    // Actions - could add status later
  }

  /**
   * Calculate AI section status based on available AI configurations
   */
  private calculateAIStatus(): NavStatusInfo {
    if (!this.selectedOrg) return { status: 'none', tooltip: '' };

    // Check for active AI configurations
    const activeConfigs = this.aiConfigurations.filter(c => c.Status === 'Active');

    if (activeConfigs.length === 0) {
      return { status: 'warning', tooltip: 'No active AI configurations' };
    }

    return { status: 'good', tooltip: `${activeConfigs.length} AI configurations available` };
  }

  /**
   * Calculate Channels section status
   */
  private calculateChannelsStatus(): NavStatusInfo {
    if (!this.selectedOrg) return { status: 'none', tooltip: '' };

    const orgChannels = this.getOrgChannels(this.selectedOrg);

    if (orgChannels.length === 0) {
      return { status: 'warning', tooltip: 'No channels configured' };
    }

    const activeChannels = orgChannels.filter(c => c.Status === 'Active');
    if (activeChannels.length === 0) {
      return { status: 'error', tooltip: 'No active channels' };
    }

    if (activeChannels.length < orgChannels.length) {
      return { status: 'warning', tooltip: `${orgChannels.length - activeChannels.length} inactive channels` };
    }

    return { status: 'good', tooltip: `${activeChannels.length} active channels` };
  }

  /**
   * Handle status change events from child admin panel components.
   * Child components emit their calculated status which is then cached
   * for display in the navigation sidebar.
   */
  onPanelStatusChange(sectionId: string, status: AdminPanelStatus): void {
    this.statusCache.set(sectionId, {
      status: status.status,
      tooltip: status.tooltip
    });
  }

  // ========== Organization Management ==========

  /**
   * Start creating a new root organization
   */
  startCreateOrg(): void {
    this.isCreatingOrg = true;
    this.isEditingOrg = false;
    this.editingOrgId = null;
    this.orgForm = { name: '', parentId: null, description: '' };
    this.orgModalError = '';
  }

  /**
   * Start creating a sub-organization under an existing org
   */
  startCreateSubOrg(parentOrg: mjBizAppsCommonOrganizationEntity): void {
    this.isCreatingOrg = true;
    this.isEditingOrg = false;
    this.editingOrgId = null;
    this.orgForm = { name: '', parentId: parentOrg.ID, description: '' };
    this.orgModalError = '';
  }

  /**
   * Start editing an existing organization
   */
  startEditOrg(org: mjBizAppsCommonOrganizationEntity): void {
    this.isEditingOrg = true;
    this.isCreatingOrg = false;
    this.editingOrgId = org.ID;
    this.orgForm = {
      name: org.Name,
      parentId: org.ParentID || null,
      description: org.Description || ''
    };
    this.orgModalError = '';
  }

  /**
   * Cancel org creation/editing
   */
  cancelOrgEdit(): void {
    this.isCreatingOrg = false;
    this.isEditingOrg = false;
    this.editingOrgId = null;
    this.orgForm = { name: '', parentId: null, description: '' };
    this.orgModalError = '';
  }

  /**
   * Get organizations that can be selected as parents
   * (exclude the org being edited and its descendants to prevent cycles)
   */
  getAvailableParentOrgs(): mjBizAppsCommonOrganizationEntity[] {
    if (!this.editingOrgId) {
      return this.organizations;
    }

    // Get all descendant IDs of the org being edited
    const descendantIds = new Set<string>();
    const getDescendants = (orgId: string) => {
      for (const org of this.organizations) {
        if (org.ParentID === orgId) {
          descendantIds.add(org.ID);
          getDescendants(org.ID);
        }
      }
    };
    descendantIds.add(this.editingOrgId);
    getDescendants(this.editingOrgId);

    // Exclude the org and its descendants
    return this.organizations.filter(o => !descendantIds.has(o.ID));
  }

  /**
   * Save the organization (create or update)
   */
  async saveOrg(): Promise<void> {
    if (!this.orgForm.name.trim()) {
      this.orgModalError = 'Organization name is required.';
      return;
    }

    this.isSavingOrg = true;
    this.orgModalError = '';

    try {
      const md = new Metadata();

      if (this.isCreatingOrg) {
        // Create new organization
        const newOrg = await md.GetEntityObject<mjBizAppsCommonOrganizationEntity>('MJ_BizApps_Common: Organizations');
        newOrg.Name = this.orgForm.name.trim();
        newOrg.ParentID = this.orgForm.parentId;
        newOrg.Description = this.orgForm.description.trim() || null;
        newOrg.Status = 'Active';

        const result = await newOrg.Save();
        if (!result) {
          throw new Error('Failed to create organization');
        }

        // Auto-add the creator as an Owner member (v1.3 BAC + OrganizationRole)
        const currentPersonId = IzzyMetadataEngine.Instance.CurrentContactID;
        if (currentPersonId) {
          const roleRv = new RunView();
          const roleResult = await roleRv.RunView<BCSaaSOrganizationRoleEntity>({
            EntityName: 'BC: Organization Roles',
            ExtraFilter: "DisplayName = 'Owner'"
          });
          if (roleResult.Success && roleResult.Results.length > 0) {
            await BCMembershipService.CreateMembership(
              currentPersonId,
              newOrg.ID,
              roleResult.Results[0].ID,
              Metadata.Provider.CurrentUser
            );
          }
        }

        this.orgModalSuccess = `Organization "${newOrg.Name}" created successfully.`;
      } else if (this.isEditingOrg && this.editingOrgId) {
        // Update existing organization
        const rv = new RunView();
        const orgResult = await rv.RunView<mjBizAppsCommonOrganizationEntity>({
          EntityName: 'MJ_BizApps_Common: Organizations',
          ExtraFilter: `ID = '${this.editingOrgId}'`,
          ResultType: 'entity_object'
        });

        if (!orgResult.Success || orgResult.Results.length === 0) {
          throw new Error('Organization not found');
        }

        const org = orgResult.Results[0];
        org.Name = this.orgForm.name.trim();
        org.ParentID = this.orgForm.parentId;
        org.Description = this.orgForm.description.trim() || null;

        const result = await org.Save();
        if (!result) {
          throw new Error('Failed to update organization');
        }

        this.orgModalSuccess = `Organization "${org.Name}" updated successfully.`;
      }

      // Refresh data
      await IzzyMetadataEngine.Instance.Config(true, md.CurrentUser);

      // Creating an org auto-adds the user as Owner — re-resolve admin orgs so the
      // new org passes the dropdown filter below.
      await this.loadAdminOrgIds();
      this.organizations = this.getUserOrganizations();

      this.buildOrgTree();

      // Refresh the org selector dropdown so it shows the new org
      await this.orgSelector?.refresh();

      this.cancelOrgEdit();

      // Clear success message after delay
      setTimeout(() => { this.orgModalSuccess = ''; }, 3000);

    } catch (error) {
      console.error('Error saving organization:', error);
      this.orgModalError = 'Failed to save organization. Please try again.';
    } finally {
      this.isSavingOrg = false;
    }
  }

  /**
   * Show delete confirmation dialog
   */
  confirmDeleteOrg(node: OrgTreeNode): void {
    this.deleteOrgId = node.org.ID;
    this.deleteOrgName = node.org.Name;
    this.confirmDeleteOrgVisible = true;
  }

  /**
   * Cancel delete confirmation
   */
  cancelDeleteOrg(): void {
    this.confirmDeleteOrgVisible = false;
    this.deleteOrgId = null;
    this.deleteOrgName = '';
  }

  /**
   * Execute org deletion
   */
  async executeDeleteOrg(): Promise<void> {
    if (!this.deleteOrgId) return;

    this.isSavingOrg = true;
    this.orgModalError = '';

    try {
      const rv = new RunView();
      const orgResult = await rv.RunView<mjBizAppsCommonOrganizationEntity>({
        EntityName: 'MJ_BizApps_Common: Organizations',
        ExtraFilter: `ID = '${this.deleteOrgId}'`,
        ResultType: 'entity_object'
      });

      if (!orgResult.Success || orgResult.Results.length === 0) {
        throw new Error('Organization not found');
      }

      const org = orgResult.Results[0];
      const orgName = org.Name;

      const result = await org.Delete();
      if (!result) {
        throw new Error('Cannot delete organization. Please remove all members and channels first.');
      }

      // Update selection if deleted org was selected
      if (this.selectedOrg?.ID === this.deleteOrgId) {
        this.selectedOrg = this.organizations.find(o => o.ID !== this.deleteOrgId) || null;
        this.selectedChannel = null;
        this.saveUserSettings();
      }

      // Refresh data
      const md = new Metadata();
      await IzzyMetadataEngine.Instance.Config(true, md.CurrentUser);
      this.organizations = this.getUserOrganizations();

      this.buildOrgTree();

      // Refresh the org selector dropdown
      await this.orgSelector?.refresh();

      this.orgModalSuccess = `Organization "${orgName}" deleted successfully.`;
      this.cancelDeleteOrg();

      // Clear success message after delay
      setTimeout(() => { this.orgModalSuccess = ''; }, 3000);

    } catch (error) {
      console.error('Error deleting organization:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to delete organization.';
      this.orgModalError = errorMsg;
    } finally {
      this.isSavingOrg = false;
    }
  }
}

export function LoadAdministrationComponent(): void {}
